import React, { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Dialog,
  Field,
  Fieldset,
  Flex,
  GridItem,
  HStack,
  Input,
  SimpleGrid,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { Button } from "components/ui/Button";
import AppAvatar from "components/utils/AppAvatar";
import StatCard from "components/utils/StatCard";
import InstructorPerformanceCharts from "./PerformanceCharts";
import {
  MdGroupAdd,
  MdOutlineCheckCircle,
  MdOutlineGroups,
  MdOutlinePeople,
  MdOutlineSupervisorAccount,
  MdSearch,
} from "react-icons/md";
import {
  useCreateDashboardInstructor,
  useDashboardInstructores,
} from "features/dashboard/instructores/hooks";
import { getAvatarHexColor, resolveAvatarUrl } from "utils/avatar";

const DEFAULT_INSTRUCTOR_ROLE = "Instructor";
const ROLE_BADGE_PALETTES = {
  Instructor: "blue",
  "Jefe de Nivel": "red",
  "Subjefe de Nivel": "orange",
};

const COLS = ["", "Nombre", "Asistencias", "Preceptorias"];
const COL_TEMPLATE = "40px 1fr 110px 110px";

const toNull = (v) => {
  const s = String(v ?? "").trim();
  return s.length ? s : null;
};

function InstructorRow({ item }) {
  const [hov, setHov] = useState(false);

  return (
    <Box
      as={Link}
      to={`/dashboard/club/instructores/${item.id}`}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      display="grid"
      gridTemplateColumns={COL_TEMPLATE}
      alignItems="center"
      gap="3"
      px="3.5"
      py="2.5"
      bg={hov ? "dashboard.bg.surfaceMuted" : "dashboard.bg.surface"}
      borderRadius="12px"
      border="1.5px solid transparent"
      transition="all 0.15s"
      cursor="pointer"
      textDecoration="none"
    >
      <AppAvatar name={item.nombre} src={item.avatarSrc} color={item.avatarColor} size={36} />

      <Box minW="0">
        <Text fontSize="sm" fontWeight="700" color="dashboard.fg.default" lineHeight="1.2" truncate>
          {item.nombre}
        </Text>
        <Badge
          mt="1"
          colorPalette={ROLE_BADGE_PALETTES[item.role] || "gray"}
          variant="subtle"
          rounded="full"
          px="2.5"
          py="0.5"
          fontWeight="700"
        >
          {item.role}
        </Badge>
      </Box>

      <Text fontSize="sm" fontWeight="700" color="dashboard.fg.default">
        {item.asistenciasCount}
      </Text>

      <Text fontSize="sm" fontWeight="700" color="dashboard.fg.default">
        {item.preceptoriasCount}
      </Text>
    </Box>
  );
}

const Instructores = () => {
  const q = useDashboardInstructores();
  const createM = useCreateDashboardInstructor();
  const [search, setSearch] = useState("");

  const items = useMemo(() => {
    const rows = Array.isArray(q.data) ? q.data : [];
    return rows.map((row) => {
      const id = row?.instructorId;
      const nombre = row?.instructorNombre?.trim() || "Instructor sin nombre";
      const role = row?.instructorRol?.trim() || DEFAULT_INSTRUCTOR_ROLE;

      return {
        id,
        nombre,
        role,
        avatarSrc: resolveAvatarUrl(row) || null,
        asistenciasCount: Number(row?.asistenciasCount ?? 0),
        preceptoriasCount: Number(row?.preceptoriasCount ?? 0),
        actividadesTotal: Number(row?.actividadesTotal ?? 0),
        aTiempoCount: Number(row?.aTiempoCount ?? 0),
        tardeCount: Number(row?.tardeCount ?? 0),
        tempranoCount: Number(row?.tempranoCount ?? 0),
        tardeTempranoCount: Number(row?.tardeTempranoCount ?? 0),
        remplazosCount: Number(row?.remplazosCount ?? 0),
        faltasCount: Math.max(
          0,
          Number(row?.actividadesTotal ?? 0) - Number(row?.asistenciasCount ?? 0),
        ),
        avatarColor: getAvatarHexColor(id || nombre),
      };
    }).filter((item) => item.id);
  }, [q.data]);

  const total = items.length;
  const totalAsistencias = useMemo(
    () => items.reduce((sum, item) => sum + item.asistenciasCount, 0),
    [items],
  );
  const totalPreceptorias = useMemo(
    () => items.reduce((sum, item) => sum + item.preceptoriasCount, 0),
    [items],
  );
  const activePreceptores = useMemo(
    () => items.filter((item) => item.preceptoriasCount > 0).length,
    [items],
  );
  const filteredItems = useMemo(
    () =>
      items.filter((item) => {
        if (search && !item.nombre.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
      }),
    [items, search],
  );

  const sortedItems = useMemo(
    () =>
      [...filteredItems].sort((a, b) => {
        return b.asistenciasCount - a.asistenciasCount || a.nombre.localeCompare(b.nombre);
      }),
    [filteredItems],
  );

  const [open, setOpen] = useState(false);
  const [fNombre, setFNombre] = useState("");
  const [fFecha, setFFecha] = useState("");
  const [fTelefono, setFTelefono] = useState("");
  const [fGeneracion, setFGeneracion] = useState("");

  const saving = createM.isPending;
  const error = createM.error;

  const resetForm = () => {
    setFNombre("");
    setFFecha("");
    setFTelefono("");
    setFGeneracion("");
  };

  const openCreate = () => {
    resetForm();
    setOpen(true);
  };

  const onSave = async () => {
    await createM.mutateAsync({
      nombre: toNull(fNombre),
      fechaNacimiento: toNull(fFecha),
      telefono: toNull(fTelefono),
      generacion: toNull(fGeneracion),
    });
    setOpen(false);
  };

  return (
    <Box>
      <Flex
        direction={{ base: "column", md: "row" }}
        align={{ base: "stretch", md: "center" }}
        justify="space-between"
        gap="4"
        mb="6"
      >
        <Box>
          <Text
            fontFamily="display"
            fontWeight="700"
            fontSize={{ base: "2xl", md: "3xl" }}
            color="dashboard.fg.default"
            lineHeight="1.1"
          >
            Instructores
          </Text>
          <Text fontSize="sm" color="dashboard.fg.muted" mt="0.5">
            Revisa asistencia y preceptorias por instructor
          </Text>
        </Box>

        <Button color="red" variant="solid" radius="xl" fontSize="lg" onClick={openCreate} alignSelf={{ base: "stretch", md: "auto" }}>
          <MdGroupAdd />
          <Text>Agregar Instructor</Text>
        </Button>
      </Flex>

      {q.isError ? (
        <pre style={{ whiteSpace: "pre-wrap", color: "crimson", marginBottom: 12 }}>
          {String(q.error?.message || q.error)}
        </pre>
      ) : null}

      <SimpleGrid columns={[2, 4]} gap="3" pb="6">
        <StatCard title="Instructores Registrados" data={String(total)} icon={MdOutlinePeople} tone="info" />
        <StatCard title="Asistencias" data={String(totalAsistencias)} icon={MdOutlineCheckCircle} tone="success" />
        <StatCard title="Preceptorias" data={String(totalPreceptorias)} icon={MdOutlineSupervisorAccount} tone="warning" />
        <StatCard title="Preceptores Activos" data={String(activePreceptores)} icon={MdOutlineGroups} tone="danger" />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 12 }} gap="4" alignItems="start">
        <GridItem colSpan={{ base: 1, lg: 5 }} position={{ lg: "sticky" }} top={{ lg: "5" }}>
          <InstructorPerformanceCharts data={items} />
        </GridItem>

        <GridItem colSpan={{ base: 1, lg: 7 }}>
          <Box display="flex" flexDirection="column" gap="3" minH="0">
            <HStack gap="2.5" flexWrap="wrap" flexShrink={0}>
              <Box position="relative" maxW="280px" flex={1}>
                <Box
                  position="absolute"
                  left="10px"
                  top="50%"
                  transform="translateY(-50%)"
                  color="dashboard.fg.subtle"
                  pointerEvents="none"
                  fontSize="md"
                >
                  <MdSearch />
                </Box>
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar instructor..."
                  pl="8"
                  fontSize="sm"
                  fontWeight="500"
                  bg="dashboard.bg.surface"
                  border="1.5px solid"
                  borderColor="dashboard.border.default"
                  borderRadius="10px"
                  _focus={{ borderColor: "arawaks.campfireRed", outline: "none" }}
                />
              </Box>

            </HStack>

            <Box overflowX="auto">
              <Box minW="440px">
                <Box display="grid" gridTemplateColumns={COL_TEMPLATE} gap="3" px="3.5" flexShrink={0}>
                  {COLS.map((h) => (
                    <Text
                      key={h}
                      fontSize="10px"
                      fontWeight="800"
                      color="dashboard.fg.subtle"
                      letterSpacing="0.06em"
                      textTransform="uppercase"
                    >
                      {h}
                    </Text>
                  ))}
                </Box>

                <Box mt="2" display="flex" flexDirection="column" gap="1">
                  {q.isLoading ? (
                    <Box py="10" display="flex" justifyContent="center">
                      <Spinner />
                    </Box>
                  ) : sortedItems.length === 0 ? (
                    <Box py="12" display="flex" alignItems="center" justifyContent="center">
                      <Text fontSize="md" fontWeight="600" color="dashboard.fg.subtle">
                        No hay instructores registrados
                      </Text>
                    </Box>
                  ) : (
                    sortedItems.map((item) => (
                      <InstructorRow key={item.id} item={item} />
                    ))
                  )}
                </Box>
              </Box>
            </Box>

            <Text fontSize="xs" color="dashboard.fg.subtle" fontWeight="500" flexShrink={0}>
              {sortedItems.length} instructor{sortedItems.length !== 1 ? "es" : ""}
            </Text>
          </Box>
        </GridItem>
      </SimpleGrid>

      <Dialog.Root open={open} onOpenChange={(e) => setOpen(e.open)}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            maxW="lg"
            bg="dashboard.bg.dialog"
            color="dashboard.fg.default"
            borderWidth="1px"
            borderColor="dashboard.border.default"
          >
            <Dialog.Header>
              <Dialog.Title>Agregar Instructor</Dialog.Title>
            </Dialog.Header>

            <Dialog.Body>
              <Fieldset.Root size="lg">
                <Fieldset.Content>
                  <Field.Root>
                    <Field.Label>Nombre</Field.Label>
                    <Input
                      value={fNombre}
                      onChange={(e) => setFNombre(e.target.value)}
                      placeholder="Ej. Jorge de la Garza"
                    />
                  </Field.Root>

                  <Field.Root>
                    <Field.Label>Fecha de nacimiento</Field.Label>
                    <Input type="date" value={fFecha} onChange={(e) => setFFecha(e.target.value)} />
                  </Field.Root>

                  <Field.Root>
                    <Field.Label>Telefono</Field.Label>
                    <Input
                      type="tel"
                      value={fTelefono}
                      onChange={(e) => setFTelefono(e.target.value)}
                      placeholder="Ej. 81 1234 5678"
                    />
                  </Field.Root>

                  <Field.Root>
                    <Field.Label>Generacion</Field.Label>
                    <Input
                      value={fGeneracion}
                      onChange={(e) => setFGeneracion(e.target.value)}
                      placeholder="Ej. 2024"
                    />
                  </Field.Root>

                  {error ? (
                    <Text color="dashboard.feedback.error" whiteSpace="pre-wrap">
                      {String(error?.message || error)}
                    </Text>
                  ) : null}
                </Fieldset.Content>
              </Fieldset.Root>
            </Dialog.Body>

            <Dialog.Footer>
              <HStack justifyContent="flex-end" w="full">
                <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
                  Cancelar
                </Button>
                <Button colorPalette="red" onClick={onSave} disabled={saving}>
                  {saving ? <Spinner size="sm" /> : null}
                  <Text>Guardar</Text>
                </Button>
              </HStack>
            </Dialog.Footer>

            <Dialog.CloseTrigger />
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </Box>
  );
};

export default Instructores;
